import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import { authAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { FaGoogle } from 'react-icons/fa';

import logger from '../utils/logger';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!email || !password) {
      const validationError = 'Please fill in all fields';
      setError(validationError);
      logger.warn('Login validation failed', { reason: 'Missing fields' });
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      const validationError = 'Please enter a valid email address';
      setError(validationError);
      logger.warn('Login validation failed', { reason: 'Invalid email format' });
      setLoading(false);
      return;
    }

    try {
      logger.info('Attempting login', { email });
      // Call backend login API
      const response = await authAPI.login(email, password);

      if (response.success) {
        // Use context login() — updates global state AND localStorage atomically
        login(response.user, response.accessToken, response.refreshToken);

        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        logger.info('Login successful', { email });
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login failed');
        logger.warn('Login failed', { email, reason: response.message });
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      logger.error('Login error', { email, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setError('');
        const response = await authAPI.googleLogin({ accessToken: tokenResponse.access_token });
        
        if (response.success) {
          login(response.user, response.accessToken, response.refreshToken);
          logger.info('Google login successful');
          navigate('/dashboard');
        }
      } catch (err) {
        setError(err.message || 'Google login failed');
        logger.error('Google login error', { error: err.message });
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setError('Google Sign In was unsuccessful');
      logger.error('Google login failed at client level');
    },
  });

  const handleGoogleSuccess = async (credentialResponse) => {

    try {
      setLoading(true);
      setError('');
      const response = await authAPI.googleLogin(credentialResponse.credential);
      
      if (response.success) {
        login(response.user, response.accessToken, response.refreshToken);
        logger.info('Google login successful');
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Google login failed');
      logger.error('Google login error', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign In was unsuccessful. Try again later');
    logger.error('Google login failed at client level');
  };

  return (
    <div className="bg-gray-950 min-h-screen">
      <Navbar hideAuthButtons={true} />
      
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold text-white mb-4 tracking-wider">SIGN IN</h1>
            <div className="w-16 h-1.5 bg-red-500 mx-auto mb-6"></div>
            <p className="text-gray-400 text-sm font-medium">
              Access your matches and connect with the community.
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-gray-900 border-2 border-gray-600 rounded-lg p-8 mb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                  Email Address
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label htmlFor="password" className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-red-500 hover:text-red-400 text-xs font-bold transition">
                    FORGOT?
                  </Link>
                </div>
                <div className="relative">
                  <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-12 pr-12 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-400 transition"
                  >
                    {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 bg-gray-800 border border-gray-700 rounded cursor-pointer accent-red-500"
                />
                <label htmlFor="remember" className="ml-3 text-xs text-gray-400 cursor-pointer font-medium">
                  Remember me on this device
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-400 text-xs">{error}</p>
                </div>
              )}

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-700 text-white font-bold py-3 px-4 rounded transition duration-200 text-sm uppercase tracking-wider mt-8"
              >
                {loading ? 'SIGNING IN...' : 'SIGN IN TO PLAY'}
              </button>

              {/* Google Sign In */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-900 px-4 text-gray-500 font-bold tracking-widest">OR SIGN IN WITH</span>
                </div>
              </div>


              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => handleGoogleLogin()}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-4 rounded transition duration-200 text-sm uppercase tracking-wider"
                >
                  <FaGoogle className="text-red-500 w-5 h-5" />
                  Sign in with Google
                </button>
              </div>

            </form>
          </div>

          {/* Register Link */}
          <div className="text-center border-t border-gray-800 pt-6">
            <p className="text-gray-400 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-red-500 hover:text-red-400 font-bold transition">
                REGISTER NOW
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
